/**
 * Cookie management utilities
 * Provides functions to set, get, and remove cookies with proper domain handling
 */

interface CookieOptions {
  domain?: string
  expires?: Date | string
  httpOnly?: boolean
  path?: string
  sameSite?: "Lax" | "None" | "Strict"
  secure?: boolean
}

/**
 * Sets a cookie with the given name and value
 * @param name - Cookie name
 * @param value - Cookie value
 * @param options - Cookie options
 * @returns boolean indicating success
 */

export const setCookie = (name: string, value: string, options: CookieOptions = {}): boolean => {
  try {
    const isHTTPS = window.location.protocol === "https:"
    const hostname = window.location.hostname
    const isLocalhost =
      hostname === "localhost" || hostname === "127.0.0.1" || hostname.includes(".localhost")

    const cookieOptions: CookieOptions = {
      path: "/",
      sameSite: "Lax",
      secure: !isLocalhost && isHTTPS,
      ...options,
    }

    let cookieString = `${name}=${encodeURIComponent(value)}`

    Object.entries(cookieOptions).forEach(([key, value]) => {
      if (value !== undefined && key !== "httpOnly") {
        cookieString += `; ${key}`
        if (value !== true) {
          cookieString += `=${value}`
        }
      }
    })

    document.cookie = cookieString

    // Verify cookie was set
    const cookieExists = document.cookie.split("; ").some((item) => item.startsWith(`${name}=`))

    return cookieExists
  } catch (error) {
    console.error("Error setting cookie:", error)
    return false
  }
}

/**
 * Gets a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */

export const getCookie = (name: string): null | string => {
  try {
    const cookies = document.cookie.split(";")
    const targetCookie = cookies.find((c) => c.trim().startsWith(`${name}=`))
    if (targetCookie) {
      const value = targetCookie.split("=")[1]?.trim()
      if (value) {
        return decodeURIComponent(value)
      }
    }
    return null
  } catch (error) {
    console.error("Error getting cookie:", error)
    return null
  }
}

/**
 * Removes a cookie by name
 * @param name - Cookie name
 */

export const removeCookie = (name: string): void => {
  try {
    const hostname = window.location.hostname
    const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1"
    const domainParts = hostname.split(".")
    const isSubdomain = domainParts.length > 2

    // Remove cookie with same domain settings as when it was set
    let cookieString = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`

    if (!isLocalhost && isSubdomain) {
      cookieString += `; domain=.${domainParts.slice(-2).join(".")}`
    } else if (isLocalhost) {
      cookieString += `; domain=localhost`
    }

    document.cookie = cookieString

    // Also try removing without domain specification as fallback
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`
  } catch (error) {
    console.error("Error removing cookie:", error)
  }
}

/**
 * Checks if a cookie exists
 * @param name - Cookie name
 * @returns boolean indicating if cookie exists
 */

export const cookieExists = (name: string): boolean => {
  return getCookie(name) !== null
}

/**
 * Gets all cookies as an object
 * @returns Object with cookie names as keys and values as values
 */

export const getAllCookies = (): Record<string, string> => {
  try {
    const cookies: Record<string, string> = {}
    document.cookie.split(";").forEach((cookie) => {
      const [name, value] = cookie.trim().split("=")
      if (name && value) {
        cookies[name] = decodeURIComponent(value)
      }
    })
    return cookies
  } catch (error) {
    console.error("Error getting all cookies:", error)
    return {}
  }
}
